<template>
  <div class="exam-result-container">
    <div class="exam-result-card" v-loading="loading">
      <template v-if="result && !loading">
        <!-- Score Circle -->
        <div class="result-header" :class="result.passed ? 'passed' : 'failed'">
          <div class="score-circle">
            <span class="score-value">{{ result.score_percentage }}%</span>
          </div>
          <h1 class="result-status">{{ result.passed ? 'Congratulations! You Passed!' : 'Not Passed' }}</h1>
          <p class="result-subtitle" v-if="result.passed && result.certificate_id">
            You have been awarded a course completion certificate.
          </p>
          <p class="result-subtitle" v-else-if="!result.passed">
            The passing score is {{ result.passing_score }}%. Keep going!
          </p>
        </div>

        <!-- Stats -->
        <div class="result-stats">
          <div class="stat-item">
            <span class="stat-label">Total Questions</span>
            <span class="stat-value">{{ result.total_questions }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Correct Answers</span>
            <span class="stat-value correct">{{ result.correct_count }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Wrong Answers</span>
            <span class="stat-value wrong">{{ result.total_questions - result.correct_count }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Time Spent</span>
            <span class="stat-value">{{ formatDuration(result.time_spent_seconds) }}</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="result-actions">
          <el-button @click="goToLearning" size="medium">Back to My Learning</el-button>
          <el-button
            v-if="result.passed && result.certificate_id"
            type="primary"
            size="medium"
            @click="goToCertificates"
          >View Certificate</el-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'
import { mapState } from 'vuex'

export default {
  name: 'ExamResult',
  data() {
    return {
      loading: true,
      result: null
    }
  },
  computed: {
    ...mapState(['user_id']),
    attemptId() {
      return this.$route.query.attempt_id
    }
  },
  mounted() {
    this.fetchResult()
  },
  methods: {
    async fetchResult() {
      this.loading = true
      try {
        const response = await postRequest('getExamAttemptDetail', {
          attempt_id: this.attemptId,
          user_id: this.user_id
        })
        if (response.data.code === 0) {
          this.result = response.data.data
        } else {
          this.$message.error(response.data.msg || 'Failed to load result')
          this.goToLearning()
        }
      } catch (error) {
        console.error('Error loading result:', error)
        this.$message.error('Failed to load exam result')
      } finally {
        this.loading = false
      }
    },
    formatDuration(seconds) {
      if (!seconds) return '0s'
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      if (m > 0) return `${m}m ${s}s`
      return `${s}s`
    },
    goToLearning() {
      this.$router.push('/my-learning')
    },
    goToCertificates() {
      this.$router.push('/my-certificates')
    }
  }
}
</script>

<style scoped>
.exam-result-container {
  min-height: 100vh;
  background: #f7f8fc;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 60px 24px;
}

.exam-result-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  max-width: 560px;
  width: 100%;
  padding: 48px;
  min-height: 300px;
  text-align: center;
}

.result-header {
  margin-bottom: 36px;
}

.score-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  border: 4px solid;
}

.passed .score-circle {
  border-color: #67C23A;
  background: #f0f9eb;
}

.failed .score-circle {
  border-color: #F56C6C;
  background: #fef0f0;
}

.score-value {
  font-size: 32px;
  font-weight: 700;
}

.passed .score-value { color: #67C23A; }
.failed .score-value { color: #F56C6C; }

.result-status {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px;
}

.passed .result-status { color: #67C23A; }
.failed .result-status { color: #F56C6C; }

.result-subtitle {
  font-size: 14px;
  color: #606266;
  margin: 0;
}

.result-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 36px;
}

.stat-item {
  background: #f7f8fc;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.stat-value {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #303133;
}

.stat-value.correct { color: #67C23A; }
.stat-value.wrong { color: #F56C6C; }

.result-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
}

@media (max-width: 768px) {
  .exam-result-container {
    padding: 24px 16px;
  }
  .exam-result-card {
    padding: 28px 20px;
  }
  .result-stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
}
</style>
